import { Dispatch, ReactNode, SetStateAction, useContext, useState } from 'react'

import { mediaAnalysisImage } from '@/common/app/modules/media/mediaAnalysis'
import { MediaAnalysisType } from '@/common/model/media/mediaAnalysis'

import Button from '@/components/view/atoms/button/Button'
import TextButton from '@/components/view/atoms/button/TextButton'
import FormCaption from '@/components/view/atoms/form/FormCaption'
import { IconType } from '@/components/view/atoms/icon/Icon'
import Tab from '@/components/view/atoms/tab/Tab'
import TabList from '@/components/view/atoms/tab/TabList'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import PointText from '@/components/view/atoms/text/PointText'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Modal, { ModalType } from '@/components/view/layouts/modal/Modal'
import ModalContent from '@/components/view/layouts/modal/ModalContent'
import MessageBar, { MessageType } from '@/components/view/molecules/bar/MessageBar'
import ModalFooter from '@/components/view/molecules/modal/ModalFooter'
import ModalHeader from '@/components/view/molecules/modal/ModalHeader'

import { MediaAnalysisHelpModalContext } from '@/components/container/modal/mediaAnalysis/MediaAnalysisHelpModalProvider'

// ---- 表示用の静的データ ----

type PrecisionRow = { item: string; subText?: string; level: '高' | '中' | '低'; note: string }

const COMPETITOR_ROWS: PrecisionRow[] = [
  { item: '業界の推測', level: '高', note: 'ドメイン名・社名から業界は比較的正確に推測できます' },
  { item: '月間流入数', level: '低', note: '実データがないため、桁レベルで異なる場合があります' },
  { item: 'キーワード順位', level: '低', note: '順位は日々変動するため、推測での再現は困難です' },
  { item: '月間検索数', level: '低', note: 'メジャーなKWは近い値が出ることもあります' },
  {
    item: 'ドメインオーソリティ',
    level: '低',
    note: '外部ツール独自の指標のため、推定が難しい領域です'
  },
  { item: '広告出稿度', level: '低', note: 'リアルタイムの広告市場データがないため参考程度です' },
  { item: 'SWOT分析', level: '中', note: '業界の一般的な傾向として参考にできます' }
]

const SERP_ROWS: PrecisionRow[] = [
  { item: 'キーワード順位', level: '高', note: '実際のGoogle検索結果から取得（自社・競合とも）' },
  { item: '月間検索数', level: '高', note: 'Google Adsのデータに基づく実数値' },
  { item: '広告出稿度', level: '高', note: 'Google Adsの競合度データ' },
  { item: '推定流入数', level: '中', note: '順位 × 検索数 × CTR推定曲線で算出' },
  { item: 'キーワード数', level: '高', note: '100位以内にランクインしたキーワード数を集計' }
]

const CONTENT_GAP_ROWS: PrecisionRow[] = [
  {
    item: 'カバレッジ判定',
    subText: '十分 / 部分 / 未対応',
    level: '中',
    note: '既存記事のタイトル・内容の網羅性からAIが判定する推定値'
  },
  {
    item: 'コンテンツギャップ件数',
    level: '中',
    note: '判定結果の集計のため、記事の見出し・内容次第で変動します'
  },
  {
    item: '推奨記事タイトル案・想定キーワード',
    level: '中',
    note: 'Google AIが生成する提案です。公開前に内容のご確認をお願いします'
  }
]

const CANNIBALIZATION_ROWS: PrecisionRow[] = [
  {
    item: '重複度（%）',
    level: '中',
    note: 'AIがタイトル・メタディスクリプションの類似性から算出する推定値'
  },
  {
    item: '深刻度',
    subText: '高 / 中 / 低',
    level: '高',
    note: '重複度から自動判定（80%以上＝高／50〜79%＝中／50%未満＝低）'
  },
  {
    item: '推奨対応',
    subText: '統合 / リダイレクト / 差別化',
    level: '中',
    note: 'AIによる提案です。実施可否は内容をご確認のうえご判断ください'
  }
]

const INTERNAL_LINK_ROWS: PrecisionRow[] = [
  {
    item: '内部リンク健全性スコア',
    level: '中',
    note: 'テーマ関連性からAIが算出する推定指標です'
  },
  {
    item: '孤立ページ',
    level: '中',
    note: '実クロールではなく推定ベースのため、目安としてご活用ください'
  },
  {
    item: '推奨内部リンク',
    subText: 'リンク元 / リンク先 / アンカーテキスト',
    level: '中',
    note: 'AIによる提案です。実装前に文脈をご確認ください'
  },
  {
    item: 'トピッククラスターの充実度',
    level: '中',
    note: 'ピラーページに対するサテライトページの充足率です'
  }
]

const CITATION_ROWS: PrecisionRow[] = [
  {
    item: '言及元ページ',
    subText: 'URL / タイトル / 説明文',
    level: '低',
    note: '実在するドメイン名（prtimes.jp、note.com等）を使ってAIがゼロから生成した推定ページです。実際にそのURLでブランドが言及されているとは限りません'
  },
  {
    item: '言及元ジャンル',
    subText: 'カテゴリ集計',
    level: '低',
    note: '上記の生成データを19カテゴリ（ニュース・メディア／比較・ランキング等）にAIが分類した集計です'
  },
  {
    item: '言及元ドメイン集計',
    level: '低',
    note: '同上。生成データの集計のため、実際のドメイン別言及数とは異なります'
  },
  {
    item: '今後のブランディング戦略',
    subText: '強み / 弱み / 施策提案',
    level: '中',
    note: '上記の集計結果をもとに、AIが一般的な傾向として分析・提案する内容です'
  }
]

const SHARE_OF_VOICE_ROWS: PrecisionRow[] = [
  {
    item: '想定質問プロンプト',
    subText: '10〜15件',
    level: '中',
    note: 'カテゴリ名からAIが生成する質問例です'
  },
  {
    item: 'ブランド言及シェア率・平均順位・TOP3率',
    level: '低',
    note: 'Claude自身が回答と言及判定の両方を行った自己分析結果です。実際のChatGPT・Gemini等での言及傾向とは異なる場合があります'
  },
  {
    item: '強い / 弱いクエリ・改善提案',
    level: '中',
    note: '上記集計結果からAIが導く傾向分析・提案です'
  }
]

const SENTIMENT_ROWS: PrecisionRow[] = [
  {
    item: '想定質問プロンプト',
    subText: '5〜8件',
    level: '中',
    note: 'ブランド名・カテゴリからAIが生成する質問例です'
  },
  {
    item: '自社センチメントスコア・分布',
    subText: '-1.0〜1.0',
    level: '低',
    note: 'Claudeが自ら回答し自己分析した結果です。実際の生成AIサービスでの評価傾向とは異なる場合があります'
  },
  {
    item: '強み・懸念点・改善提案',
    level: '中',
    note: '上記結果からAIが導く傾向分析・提案です'
  },
  {
    item: '競合ブランドのスコア比較',
    level: '低',
    note: '競合ブランドは個別に質問・分析せず、AIの一般的な評価からの推定値です。自社スコアより精度は下がります'
  }
]

const QUERY_FANOUT_ROWS: PrecisionRow[] = [
  {
    item: '会話フロー',
    subText: '質問 / AI回答要約',
    level: '中',
    note: '直前までの会話履歴をもとに、AIが応答とフォローアップ質問を生成します'
  },
  {
    item: 'ブランド持続性スコア',
    subText: '0〜1.0',
    level: '低',
    note: 'Claude自身が回答と言及判定を行った自己分析結果の割合です。実際の生成AIサービスでの傾向とは異なる場合があります'
  },
  {
    item: '脱落ポイント',
    level: '低',
    note: '持続性スコアと同じ自己分析結果に基づく判定です'
  },
  {
    item: '最強 / 最弱段階・改善提案',
    level: '中',
    note: '会話フロー全体の傾向からAIが導く分析・提案です'
  }
]

// ---- 共通の表示部品 ----

const TitleDescription = ({
  type,
  title,
  description
}: {
  type: MediaAnalysisType
  title: string
  description: ReactNode
}) => {
  return (
    <div className='flex items-center gap-4'>
      <img src={mediaAnalysisImage(type)} width={120} height={80} />
      <div className='flex flex-col gap-2'>
        <Text weight='bold'>{title}</Text>
        <Text size={12} tag='p'>
          {description}
        </Text>
      </div>
    </div>
  )
}

// 精度・参考度バッジ（高い/高=成功、中=注意、それ以外=重要）
const PrecisionBadge = ({ level }: { level: string }) => {
  const textColor =
    level === '高' ? TextColor.CRITICAL : level === '中' ? TextColor.KAKI : TextColor.DISABLED
  return (
    <Text size={12} weight='bold' color={textColor}>
      {level}
    </Text>
  )
}

// 項目／精度／説明 の3列テーブル
const PrecisionTable = ({
  headers,
  rows
}: {
  headers: [string, string, string]
  rows: PrecisionRow[]
}) => (
  <table className='border-secondary border-t border-b'>
    <thead>
      <TableRow head>
        <TableHeadItem type='thin'>
          <Text size={12} color={TextColor.SECONDARY}>
            {headers[0]}
          </Text>
        </TableHeadItem>
        <TableHeadItem type='thin' width={88}>
          <Text size={12} color={TextColor.SECONDARY}>
            {headers[1]}
          </Text>
        </TableHeadItem>
        <TableHeadItem type='thin'>
          <Text size={12} color={TextColor.SECONDARY}>
            {headers[2]}
          </Text>
        </TableHeadItem>
      </TableRow>
    </thead>
    <tbody>
      {rows.map((r) => (
        <TableRow key={r.item}>
          <TableRowItem>
            <Text size={12} weight='bold'>
              {r.item}
            </Text>
            {r.subText && (
              <Text size={12} color={TextColor.SECONDARY}>
                {r.subText}
              </Text>
            )}
          </TableRowItem>
          <TableRowItem width={88}>
            <PrecisionBadge level={r.level} />
          </TableRowItem>
          <TableRowItem>
            <Text size={12}>{r.note}</Text>
          </TableRowItem>
        </TableRow>
      ))}
    </tbody>
  </table>
)

// ---- 各タブの本文（SEO分析） ----

const CompetitorHelp = ({ setTab }: HelpTabBodyProps) => (
  <div className='flex flex-col gap-4'>
    <TitleDescription
      type={MediaAnalysisType.COMPETITOR}
      title='競合分析（AI推定値）'
      description={
        <>
          AIがドメイン名から業界・SEO関連の数値を推定生成します。実際の検索データではなく、あくまでAIによる推定値です。
          <br />
          競合が上位表示しているのに自社が未対応のキーワード（競合コンテンツギャップ）の検出と、優先度付きの推奨記事タイトル案の提示もあわせて行います。
        </>
      }
    />

    <div className='flex flex-col gap-1'>
      <Text weight='bold'>各項目の精度目安</Text>
      <PrecisionTable headers={['データ', '精度', '説明']} rows={COMPETITOR_ROWS} />
    </div>

    <div className='flex flex-col gap-2'>
      <MessageBar type={MessageType.WARNING} fillIn>
        <Text size={12} weight='bold' color={TextColor.CAUTION}>
          AI推定値について
        </Text>
        <Text size={12} lineHeight={150} tag='p'>
          AI推定のため、同じ条件でも毎回類似した傾向の数値が生成されます。数値そのものより、業界全体の傾向やキーワードの方向性、コンテンツの手薄な領域を把握する目的でご活用ください。
          <br />
          競合コンテンツギャップの検索Vol・競合順位も上表と同じくAIによる推定値のため、優先度づけの参考としてご活用ください。
        </Text>
      </MessageBar>
      <FormCaption size={12}>
        正確な実データが必要な場合は「
        <TextButton onClick={() => setTab('serp')}>SERP分析</TextButton>
        」をご利用ください。
      </FormCaption>
    </div>
  </div>
)

const SerpHelp = () => (
  <div className='flex flex-col gap-4'>
    <TitleDescription
      type={MediaAnalysisType.COMPETITOR_SERP}
      title='SERP実データ分析'
      description={
        <>
          Google検索の実データに基づいた競合分析です。指定したキーワードで実際にGoogle検索を行い、自社・競合の順位を取得します。競合分析（AI推定）を実データで裏付ける、最も精度の高い競合把握手段です。
        </>
      }
    />
    <div className='flex flex-col gap-1'>
      <Text weight='bold'>取得できるデータ</Text>
      <PrecisionTable headers={['データ', '精度', '説明']} rows={SERP_ROWS} />
    </div>
    <MessageBar type='success' iconType={IconType.BEGINNER}>
      <Text size={12} weight='bold' color={TextColor.SUCCESS} lineHeight={150}>
        活用のポイント
      </Text>
      <PointText
        size={12}
        texts={[
          '競合との順位比較：同じキーワードで自社と競合の順位を並べて比較できます',
          '狙うべきキーワードの発見：検索ボリュームが高く、自社の順位が低いキーワードを特定できます',
          '推定流入数の把握：順位と検索数から、各ドメインの推定流入数を算出します'
        ]}
      />
    </MessageBar>
    <MessageBar type={MessageType.WARNING} fillIn>
      <Text size={12} weight='bold' color={TextColor.CAUTION} lineHeight={150}>
        検索順位の取得について
      </Text>
      <Text size={12} lineHeight={150} tag='p'>
        シードワードを元に関連キーワードを自動取得し、最大50件の検索順位を取得します。{' '}
      </Text>
    </MessageBar>
  </div>
)

const ContentGapHelp = () => (
  <div className='flex flex-col gap-4'>
    <TitleDescription
      type={MediaAnalysisType.CONTENT_GAP}
      title='コンテンツギャップ分析'
      description={
        <>
          自社サイトの既存記事が、狙うべきテーマ・キーワードをどこまでカバーできているかをAIが判定します。「十分にカバー済み」「部分的にカバー」「全くカバーされていない」の3段階で可視化し、未対応領域には優先度付きの推奨記事タイトル案・想定キーワードまで提示します。
        </>
      }
    />
    <div className='flex flex-col gap-1'>
      <Text weight='bold'>取得できるデータ</Text>
      <PrecisionTable headers={['データ', '精度', '説明']} rows={CONTENT_GAP_ROWS} />
    </div>
    <MessageBar type='success' iconType={IconType.BEGINNER}>
      <Text size={12} weight='bold' color={TextColor.SUCCESS} lineHeight={150}>
        活用のポイント
      </Text>
      <PointText
        size={12}
        texts={[
          '前回との差分比較で、カバレッジの改善状況を確認できます',
          '優先度・検索ボリュームの高いテーマから記事化を検討できます'
        ]}
      />
    </MessageBar>
    <MessageBar type={MessageType.WARNING} fillIn>
      <Text size={12} weight='bold' color={TextColor.CAUTION} lineHeight={150}>
        AI判定について
      </Text>
      <Text size={12} lineHeight={150} tag='p'>
        判定はAI（Claude）による推定です。同じ記事でも見出しや文言の変化により判定結果が変わる場合があります。
      </Text>
    </MessageBar>
  </div>
)

const CannibalizationHelp = () => (
  <div className='flex flex-col gap-4'>
    <TitleDescription
      type={MediaAnalysisType.CANNIBALIZATION}
      title='カニバリゼーション分析'
      description={
        <>
          自社の特集記事同士のタイトル・メタディスクリプションの類似性をAIが比較し、キーワードの重複（カニバリゼーション）を検出します。重複度に応じて、統合・リダイレクト・差別化などの対応方針を提案します。
        </>
      }
    />
    <div className='flex flex-col gap-1'>
      <Text weight='bold'>取得できるデータ</Text>
      <PrecisionTable headers={['データ', '精度', '説明']} rows={CANNIBALIZATION_ROWS} />
    </div>
    <MessageBar type='success' iconType={IconType.BEGINNER}>
      <Text size={12} weight='bold' color={TextColor.SUCCESS} lineHeight={150}>
        活用のポイント
      </Text>
      <PointText
        size={12}
        texts={[
          '統合・リダイレクトすべき記事の特定',
          '新規記事作成前の重複チェック（運用フローへの組み込み）'
        ]}
      />
    </MessageBar>
    <MessageBar type={MessageType.WARNING} fillIn>
      <Text size={12} weight='bold' color={TextColor.CAUTION} lineHeight={150}>
        分析条件について
      </Text>
      <Text size={12} lineHeight={150} tag='p'>
        重複度0.3未満のペアは対象外です。前回分析からの増減や、対応状況の評価スコアも時系列で確認できます。
      </Text>
    </MessageBar>
  </div>
)

const LinkStructureHelp = () => (
  <div className='flex flex-col gap-4'>
    <TitleDescription
      type={MediaAnalysisType.LINK_STRUCTURE}
      title='内部リンク分析'
      description={
        <>
          自社の特集記事群を対象に、テーマ・タイトルの関連性からAIが理想的な内部リンク構造を分析します（実際のクロールではなく推定ベースです）。孤立ページの発見や、ピラーページとサテライトページから成るトピッククラスターの充実度を可視化し、具体的なリンク元・リンク先・アンカーテキストまで提案します。
        </>
      }
    />
    <div className='flex flex-col gap-1'>
      <Text weight='bold'>取得できるデータ</Text>
      <PrecisionTable headers={['データ', '精度', '説明']} rows={INTERNAL_LINK_ROWS} />
    </div>
    <MessageBar type='success' iconType={IconType.BEGINNER}>
      <Text size={12} weight='bold' color={TextColor.SUCCESS} lineHeight={150}>
        活用のポイント
      </Text>
      <PointText
        size={12}
        texts={[
          '孤立ページへのリンク追加',
          '推奨内部リンクの実装',
          '未作成サテライト記事の優先着手'
        ]}
      />
    </MessageBar>
    <MessageBar type={MessageType.WARNING} fillIn>
      <Text size={12} weight='bold' color={TextColor.CAUTION} lineHeight={150}>
        AI推定値について
      </Text>
      <Text size={12} lineHeight={150} tag='p'>
        実際のクロール結果ではなく、AIによる推定分析です。前回実行からのスコア推移も確認できます。
      </Text>
    </MessageBar>
  </div>
)

// ---- 各タブの本文（GEO/LLMO分析） ----

const CitationHelp = () => (
  <div className='flex flex-col gap-4'>
    <TitleDescription
      type={MediaAnalysisType.CITATION}
      title='サイテーション分析（AI生成シミュレーション）'
      description={
        <>
          自社ブランド（および任意で競合1社）の名称とURLをもとに、AIがWeb上で言及されていそうなページを推定生成し、ジャンル・ドメイン別に集計したうえで、今後のブランディング戦略を提案します。実際の検索エンジンのクロールやバックリンク取得は行っていません。
        </>
      }
    />
    <div className='flex flex-col gap-1'>
      <Text weight='bold'>各項目の精度目安</Text>
      <PrecisionTable headers={['項目', '参考度', '補足']} rows={CITATION_ROWS} />
    </div>
    <MessageBar type='success' iconType={IconType.BEGINNER}>
      <Text size={12} weight='bold' color={TextColor.SUCCESS} lineHeight={150}>
        活用のポイント
      </Text>
      <PointText
        size={12}
        texts={[
          '自社にはない競合の言及元ジャンル・ドメインの傾向把握',
          '言及元カテゴリからブランディング施策のヒントを得る'
        ]}
      />
    </MessageBar>
    <MessageBar type={MessageType.WARNING} fillIn>
      <Text size={12} weight='bold' color={TextColor.CAUTION} lineHeight={150}>
        AIシミュレーションについて
      </Text>
      <Text size={12} lineHeight={150} tag='p'>
        本分析は実際のGoogle検索結果やバックリンクデータを取得するものではなく、AIがブランド名・URLから「言及されていそうなページ」を1件ずつ生成するシミュレーションです。URLや件数の正確性よりも、言及されやすいジャンル・ドメインの傾向をつかむ目的でご活用ください。分析頻度は月1回程度で十分です。
      </Text>
    </MessageBar>
  </div>
)

const ShareOfVoiceHelp = () => (
  <div className='flex flex-col gap-4'>
    <TitleDescription
      type={MediaAnalysisType.SHARE_OF_VOICE}
      title='シェア・オブ・ボイス分析（AIシミュレーション）'
      description={
        <>
          「AIに何か聞いたとき、自社ブランドがどれくらいの確率で名前を出してもらえるか」を測る分析です。指定カテゴリでユーザーがAIアシスタントに尋ねそうな質問（10〜15個）をAIが自動生成し、その質問にAI自身が回答したうえで、自社・競合ブランドの言及有無・順位を分析してシェア率を算出します。
        </>
      }
    />
    <div className='flex flex-col gap-1'>
      <Text weight='bold'>各項目の精度目安</Text>
      <PrecisionTable headers={['データ', '精度', '説明']} rows={SHARE_OF_VOICE_ROWS} />
    </div>
    <MessageBar type='success' iconType={IconType.BEGINNER}>
      <Text size={12} weight='bold' color={TextColor.SUCCESS} lineHeight={150}>
        活用のポイント
      </Text>
      <PointText
        size={12}
        texts={[
          '「AIの世界での自社シェア」を数値で把握する参考指標として活用',
          '自社が弱いクエリ傾向を確認し、コンテンツ強化の方向性を検討'
        ]}
      />
    </MessageBar>
    <MessageBar type={MessageType.WARNING} fillIn>
      <Text size={12} weight='bold' color={TextColor.CAUTION} lineHeight={150}>
        AIシミュレーションについて
      </Text>
      <Text size={12} lineHeight={150} tag='p'>
        実際にChatGPTやGemini、Perplexityなど外部の生成AIサービスに問い合わせた結果ではなく、Claudeが質問への回答とブランド言及の判定を自己完結で行うシミュレーションです。競合ブランドは最大5社まで指定できます。
      </Text>
    </MessageBar>
  </div>
)

const SentimentHelp = () => (
  <div className='flex flex-col gap-4'>
    <TitleDescription
      type={MediaAnalysisType.SENTIMENT}
      title='センチメント分析（AIシミュレーション）'
      description={
        <>
          「AIが自社ブランドについて話すとき、褒めているのか・けなしているのか・淡々と紹介しているだけか」を判定する分析です。ブランドの評判に関する質問（5〜8個）をAIが自動生成し、その質問にAIが回答した上で、回答内容のポジティブ／ネガティブ傾向とスコアを自己分析します。
        </>
      }
    />
    <div className='flex flex-col gap-1'>
      <Text weight='bold'>各項目の精度目安</Text>
      <PrecisionTable headers={['データ', '精度', '説明']} rows={SENTIMENT_ROWS} />
    </div>
    <MessageBar type='success' iconType={IconType.BEGINNER}>
      <Text size={12} weight='bold' color={TextColor.SUCCESS} lineHeight={150}>
        活用のポイント
      </Text>
      <PointText
        size={12}
        texts={[
          'AIが自社をどのようなトーン（推奨／中立／慎重／批判）で語っているかの傾向把握',
          'ネガティブ評価が出ている観点を改善施策の検討材料にする'
        ]}
      />
    </MessageBar>
    <MessageBar type={MessageType.WARNING} fillIn>
      <Text size={12} weight='bold' color={TextColor.CAUTION} lineHeight={150}>
        分析方法について
      </Text>
      <Text size={12} lineHeight={150} tag='p'>
        自社ブランドのスコアはAIが実際に質問へ回答した上での自己分析ですが、競合ブランドのスコアはAIの一般的な評価からの推定にとどまります。いずれも実際の外部AIサービスに問い合わせた結果ではありません。
      </Text>
    </MessageBar>
  </div>
)

const QueryFanoutHelp = () => (
  <div className='flex flex-col gap-4'>
    <TitleDescription
      type={MediaAnalysisType.QUERY_FANOUT}
      title='クエリファンアウト分析（AIシミュレーション）'
      description={
        <>
          「AIとの会話が深まっていく中で、自社が最後まで推され続けるか、途中で名前が消えるか」を追跡する分析です。「おすすめの◯◯サービスは？」という質問から始め、AIが3段階のフォローアップ質問を自動生成しながら会話を深掘りし、各段階での自社ブランドの言及有無・順位を分析します。
        </>
      }
    />
    <div className='flex flex-col gap-1'>
      <Text weight='bold'>各項目の精度目安</Text>
      <PrecisionTable headers={['データ', '精度', '説明']} rows={QUERY_FANOUT_ROWS} />
    </div>
    <MessageBar type='success' iconType={IconType.BEGINNER}>
      <Text size={12} weight='bold' color={TextColor.SUCCESS} lineHeight={150}>
        活用のポイント
      </Text>
      <PointText
        size={12}
        texts={[
          '初回質問では言及されるのに、深掘りすると消えるといった脱落傾向の把握',
          '会話の後半まで推薦され続けるための情報強化ポイントの検討'
        ]}
      />
    </MessageBar>
    <MessageBar type={MessageType.WARNING} fillIn>
      <Text size={12} weight='bold' color={TextColor.CAUTION} lineHeight={150}>
        AIシミュレーションについて
      </Text>
      <Text size={12} lineHeight={150} tag='p'>
        会話の深さは4段階（初回質問＋フォローアップ最大3回）で固定です。ChatGPTやGemini等の外部AIサービスとの実際の対話ではなく、Claude自身が質問・回答・自己分析のすべてを行うシミュレーションです。
      </Text>
    </MessageBar>
  </div>
)

// ---- タブ定義 ----

type HelpTabKey =
  | 'competitor'
  | 'gsc'
  | 'serp'
  | 'content_gap'
  | 'cannibalization'
  | 'link_structure'
  | 'citation'
  | 'share_of_voice'
  | 'sentiment'
  | 'query_fanout'

type HelpTabBodyProps = { setTab: Dispatch<SetStateAction<HelpTabKey>> }

const SEO_TABS: { key: HelpTabKey; label: string; body: (props: HelpTabBodyProps) => ReactNode }[] =
  [
    { key: 'competitor', label: '競合分析', body: CompetitorHelp },
    { key: 'serp', label: 'SERP分析', body: SerpHelp },
    { key: 'content_gap', label: 'コンテンツギャップ分析', body: ContentGapHelp },
    { key: 'cannibalization', label: 'カニバリ分析', body: CannibalizationHelp },
    { key: 'link_structure', label: '内部リンク分析', body: LinkStructureHelp }
  ]

const GEO_LLMO_TABS: {
  key: HelpTabKey
  label: string
  body: (props: HelpTabBodyProps) => ReactNode
}[] = [
  { key: 'citation', label: 'サイテーション分析', body: CitationHelp },
  { key: 'share_of_voice', label: 'シェア・オブ・ボイス分析', body: ShareOfVoiceHelp },
  { key: 'sentiment', label: 'センチメント分析', body: SentimentHelp },
  { key: 'query_fanout', label: 'クエリファンアウト分析', body: QueryFanoutHelp }
]

// タブ本体。isGeoLlmoでSEO分析/GEO・LLMO分析いずれのタブ群を出すかを切り替える。
// 開くたびに key で再マウントすることで、常に各タブ群の先頭タブを初期選択にする
const MediaAnalysisHelpBody = ({ isGeoLlmo, onClose }: { isGeoLlmo: boolean; onClose(): void }) => {
  const tabs = isGeoLlmo ? GEO_LLMO_TABS : SEO_TABS
  const [tab, setTab] = useState<HelpTabKey>(tabs[0].key)
  const activeTab = tabs.find((t) => t.key === tab) ?? tabs[0]
  const Body = activeTab.body

  return (
    <>
      <TabList border>
        {tabs.map((t) => (
          <Tab key={t.key} selected={tab === t.key} onClick={() => setTab(t.key)}>
            {t.label}
          </Tab>
        ))}
      </TabList>

      <ModalContent>
        <Body setTab={setTab} />
      </ModalContent>

      <ModalFooter>
        <Button color='secondary' size='large' onClick={onClose}>
          閉じる
        </Button>
      </ModalFooter>
    </>
  )
}

const MediaAnalysisHelpModal = () => {
  const { modal, showing, hide } = useContext(MediaAnalysisHelpModalContext)

  return (
    <Modal show={showing} type={ModalType.MIDDLE}>
      <ModalHeader text='各分析について' onClickClose={hide} />
      <MediaAnalysisHelpBody
        key={`${modal.isGeoLlmo}-${showing}`}
        isGeoLlmo={modal.isGeoLlmo}
        onClose={hide}
      />
    </Modal>
  )
}

export default MediaAnalysisHelpModal
